/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {SecurityContext} from '@angular/core';

import {CompileDirectiveSummary, CompileProviderMetadata, CompileTokenMetadata} from '../compile_metadata';
import {AST} from '../expression_parser/ast';
import {ParseSourceSpan} from '../parse_util';
import {LifecycleHooks} from '../private_import_core';

/**
 * An Abstract Syntax Tree node representing part of a parsed Angular template.
 */
export interface TemplateAst {
  /**
   * The source span from which this node was parsed.
   */
  sourceSpan: ParseSourceSpan;

  /**
   * Visit this node and possibly transform it.
   */
  visit(visitor: TemplateAstVisitor, context: any): any;
}
/**
 *  A segment of text within the template.
 */
export class TextAst implements TemplateAst {
/**
 * @param {?} value
 * @param {?} ngContentIndex
 * @param {?} sourceSpan
 */
constructor(
public value: string,
public ngContentIndex: number,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any { return visitor.visitText(this, context); }
}

function TextAst_tsickle_Closure_declarations() {
 /** @type {?} */
TextAst.prototype.value;
 /** @type {?} */
TextAst.prototype.ngContentIndex;
 /** @type {?} */
TextAst.prototype.sourceSpan;
}

/**
 *  A bound expression within the text of a template.
 */
export class BoundTextAst implements TemplateAst {
/**
 * @param {?} value
 * @param {?} ngContentIndex
 * @param {?} sourceSpan
 */
constructor(
public value: AST,
public ngContentIndex: number,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitBoundText(this, context);
  }
}

function BoundTextAst_tsickle_Closure_declarations() {
 /** @type {?} */
BoundTextAst.prototype.value;
 /** @type {?} */
BoundTextAst.prototype.ngContentIndex;
 /** @type {?} */
BoundTextAst.prototype.sourceSpan;
}

/**
 *  A plain attribute on an element.
 */
export class AttrAst implements TemplateAst {
/**
 * @param {?} name
 * @param {?} value
 * @param {?} sourceSpan
 */
constructor(public name: string,
public value: string,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any { return visitor.visitAttr(this, context); }
}

function AttrAst_tsickle_Closure_declarations() {
 /** @type {?} */
AttrAst.prototype.name;
 /** @type {?} */
AttrAst.prototype.value;
 /** @type {?} */
AttrAst.prototype.sourceSpan;
}

/**
 *  A binding for an element property (e.g. `[property]="expression"`) or an animation trigger (e.g.
  * `[@trigger]="stateExp"`)
 */
export class BoundElementPropertyAst implements TemplateAst {
/**
 * @param {?} name
 * @param {?} type
 * @param {?} securityContext
 * @param {?} needsRuntimeSecurityContext
 * @param {?} value
 * @param {?} unit
 * @param {?} sourceSpan
 */
constructor(
public name: string,
public type: PropertyBindingType,
public securityContext: SecurityContext,
public needsRuntimeSecurityContext: boolean,
public value: AST,
public unit: string,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitElementProperty(this, context);
  }
/**
 * @return {?}
 */
get isAnimation(): boolean { return this.type === PropertyBindingType.Animation; }
}

function BoundElementPropertyAst_tsickle_Closure_declarations() {
 /** @type {?} */
BoundElementPropertyAst.prototype.name;
 /** @type {?} */
BoundElementPropertyAst.prototype.type;
 /** @type {?} */
BoundElementPropertyAst.prototype.securityContext;
 /** @type {?} */
BoundElementPropertyAst.prototype.needsRuntimeSecurityContext;
 /** @type {?} */
BoundElementPropertyAst.prototype.value;
 /** @type {?} */
BoundElementPropertyAst.prototype.unit;
 /** @type {?} */
BoundElementPropertyAst.prototype.sourceSpan;
}

/**
 *  A binding for an element event (e.g. `(event)="handler()"`) or an animation trigger event (e.g.
  * `(@trigger.phase)="callback($event)"`).
 */
export class BoundEventAst implements TemplateAst {
/**
 * @param {?} name
 * @param {?} target
 * @param {?} phase
 * @return {?}
 */
static calcFullName(name: string, target: string, phase: string): string {
    if (target) {
      return `${target}:${name}`;
    } else if (phase) {
      return `@${name}.${phase}`;
    } else {
      return name;
    }
  }
/**
 * @param {?} name
 * @param {?} target
 * @param {?} phase
 * @param {?} handler
 * @param {?} sourceSpan
 */
constructor(
public name: string,
public target: string,
public phase: string,
public handler: AST,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitEvent(this, context);
  }
/**
 * @return {?}
 */
get fullName() { return BoundEventAst.calcFullName(this.name, this.target, this.phase); }
/**
 * @return {?}
 */
get isAnimation(): boolean { return !!this.phase; }
}

function BoundEventAst_tsickle_Closure_declarations() {
 /** @type {?} */
BoundEventAst.prototype.name;
 /** @type {?} */
BoundEventAst.prototype.target;
 /** @type {?} */
BoundEventAst.prototype.phase;
 /** @type {?} */
BoundEventAst.prototype.handler;
 /** @type {?} */
BoundEventAst.prototype.sourceSpan;
}

/**
 *  A reference declaration on an element (e.g. `let someName="expression"`).
 */
export class ReferenceAst implements TemplateAst {
/**
 * @param {?} name
 * @param {?} value
 * @param {?} sourceSpan
 */
constructor(
public name: string,
public value: CompileTokenMetadata,
public sourceSpan: ParseSourceSpan) {
  }
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitReference(this, context);
  }
}

function ReferenceAst_tsickle_Closure_declarations() {
 /** @type {?} */
ReferenceAst.prototype.name;
 /** @type {?} */
ReferenceAst.prototype.value;
 /** @type {?} */
ReferenceAst.prototype.sourceSpan;
}

/**
 *  A variable declaration on a <template> (e.g. `var-someName="someLocalName"`).
 */
export class VariableAst implements TemplateAst {
/**
 * @param {?} name
 * @param {?} value
 * @param {?} sourceSpan
 */
constructor(public name: string,
public value: string,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitVariable(this, context);
  }
}

function VariableAst_tsickle_Closure_declarations() {
 /** @type {?} */
VariableAst.prototype.name;
 /** @type {?} */
VariableAst.prototype.value;
 /** @type {?} */
VariableAst.prototype.sourceSpan;
}

/**
 *  An element declaration in a template.
 */
export class ElementAst implements TemplateAst {
/**
 * @param {?} name
 * @param {?} attrs
 * @param {?} inputs
 * @param {?} outputs
 * @param {?} references
 * @param {?} directives
 * @param {?} providers
 * @param {?} hasViewContainer
 * @param {?} children
 * @param {?} ngContentIndex
 * @param {?} sourceSpan
 * @param {?} endSourceSpan
 */
constructor(
public name: string,
public attrs: AttrAst[],
public inputs: BoundElementPropertyAst[],
public outputs: BoundEventAst[],
public references: ReferenceAst[],
public directives: DirectiveAst[],
public providers: ProviderAst[],
public hasViewContainer: boolean,
public children: TemplateAst[],
public ngContentIndex: number,
public sourceSpan: ParseSourceSpan,
public endSourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitElement(this, context);
  }
}

function ElementAst_tsickle_Closure_declarations() {
 /** @type {?} */
ElementAst.prototype.name;
 /** @type {?} */
ElementAst.prototype.attrs;
 /** @type {?} */
ElementAst.prototype.inputs;
 /** @type {?} */
ElementAst.prototype.outputs;
 /** @type {?} */
ElementAst.prototype.references;
 /** @type {?} */
ElementAst.prototype.directives;
 /** @type {?} */
ElementAst.prototype.providers;
 /** @type {?} */
ElementAst.prototype.hasViewContainer;
 /** @type {?} */
ElementAst.prototype.children;
 /** @type {?} */
ElementAst.prototype.ngContentIndex;
 /** @type {?} */
ElementAst.prototype.sourceSpan;
 /** @type {?} */
ElementAst.prototype.endSourceSpan;
}

/**
 *  A `<template>` element included in an Angular template.
 */
export class EmbeddedTemplateAst implements TemplateAst {
/**
 * @param {?} attrs
 * @param {?} outputs
 * @param {?} references
 * @param {?} variables
 * @param {?} directives
 * @param {?} providers
 * @param {?} hasViewContainer
 * @param {?} children
 * @param {?} ngContentIndex
 * @param {?} sourceSpan
 */
constructor(
public attrs: AttrAst[],
public outputs: BoundEventAst[],
public references: ReferenceAst[],
public variables: VariableAst[],
public directives: DirectiveAst[],
public providers: ProviderAst[],
public hasViewContainer: boolean,
public children: TemplateAst[],
public ngContentIndex: number,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitEmbeddedTemplate(this, context);
  }
}

function EmbeddedTemplateAst_tsickle_Closure_declarations() {
 /** @type {?} */
EmbeddedTemplateAst.prototype.attrs;
 /** @type {?} */
EmbeddedTemplateAst.prototype.outputs;
 /** @type {?} */
EmbeddedTemplateAst.prototype.references;
 /** @type {?} */
EmbeddedTemplateAst.prototype.variables;
 /** @type {?} */
EmbeddedTemplateAst.prototype.directives;
 /** @type {?} */
EmbeddedTemplateAst.prototype.providers;
 /** @type {?} */
EmbeddedTemplateAst.prototype.hasViewContainer;
 /** @type {?} */
EmbeddedTemplateAst.prototype.children;
 /** @type {?} */
EmbeddedTemplateAst.prototype.ngContentIndex;
 /** @type {?} */
EmbeddedTemplateAst.prototype.sourceSpan;
}

/**
 *  A directive property with a bound value (e.g. `*ngIf="condition").
 */
export class BoundDirectivePropertyAst implements TemplateAst {
/**
 * @param {?} directiveName
 * @param {?} templateName
 * @param {?} value
 * @param {?} sourceSpan
 */
constructor(
public directiveName: string,
public templateName: string,
public value: AST,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitDirectiveProperty(this, context);
  }
}

function BoundDirectivePropertyAst_tsickle_Closure_declarations() {
 /** @type {?} */
BoundDirectivePropertyAst.prototype.directiveName;
 /** @type {?} */
BoundDirectivePropertyAst.prototype.templateName;
 /** @type {?} */
BoundDirectivePropertyAst.prototype.value;
 /** @type {?} */
BoundDirectivePropertyAst.prototype.sourceSpan;
}

/**
 *  A directive declared on an element.
 */
export class DirectiveAst implements TemplateAst {
/**
 * @param {?} directive
 * @param {?} inputs
 * @param {?} hostProperties
 * @param {?} hostEvents
 * @param {?} sourceSpan
 */
constructor(
public directive: CompileDirectiveSummary,
public inputs: BoundDirectivePropertyAst[],
public hostProperties: BoundElementPropertyAst[],
public hostEvents: BoundEventAst[],
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitDirective(this, context);
  }
}

function DirectiveAst_tsickle_Closure_declarations() {
 /** @type {?} */
DirectiveAst.prototype.directive;
 /** @type {?} */
DirectiveAst.prototype.inputs;
 /** @type {?} */
DirectiveAst.prototype.hostProperties;
 /** @type {?} */
DirectiveAst.prototype.hostEvents;
 /** @type {?} */
DirectiveAst.prototype.sourceSpan;
}

/**
 *  A provider declared on an element
 */
export class ProviderAst implements TemplateAst {
/**
 * @param {?} token
 * @param {?} multiProvider
 * @param {?} eager
 * @param {?} providers
 * @param {?} providerType
 * @param {?} lifecycleHooks
 * @param {?} sourceSpan
 */
constructor(
public token: CompileTokenMetadata,
public multiProvider: boolean,
public eager: boolean,
public providers: CompileProviderMetadata[],
public providerType: ProviderAstType,
public lifecycleHooks: LifecycleHooks[],
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    // No visit method in the visitor for now...
    return null;
  }
}

function ProviderAst_tsickle_Closure_declarations() {
 /** @type {?} */
ProviderAst.prototype.token;
 /** @type {?} */
ProviderAst.prototype.multiProvider;
 /** @type {?} */
ProviderAst.prototype.eager;
 /** @type {?} */
ProviderAst.prototype.providers;
 /** @type {?} */
ProviderAst.prototype.providerType;
 /** @type {?} */
ProviderAst.prototype.lifecycleHooks;
 /** @type {?} */
ProviderAst.prototype.sourceSpan;
}

export type ProviderAstType = number;
export let ProviderAstType: any = {};
ProviderAstType.PublicService = 0;
ProviderAstType.PrivateService = 1;
ProviderAstType.Component = 2;
ProviderAstType.Directive = 3;
ProviderAstType.Builtin = 4;
ProviderAstType[ProviderAstType.PublicService] = "PublicService";
ProviderAstType[ProviderAstType.PrivateService] = "PrivateService";
ProviderAstType[ProviderAstType.Component] = "Component";
ProviderAstType[ProviderAstType.Directive] = "Directive";
ProviderAstType[ProviderAstType.Builtin] = "Builtin";

/**
 *  Position where content is to be projected (instance of `<ng-content>` in a template).
 */
export class NgContentAst implements TemplateAst {
/**
 * @param {?} index
 * @param {?} ngContentIndex
 * @param {?} sourceSpan
 */
constructor(
public index: number,
public ngContentIndex: number,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: TemplateAstVisitor, context: any): any {
    return visitor.visitNgContent(this, context);
  }
}

function NgContentAst_tsickle_Closure_declarations() {
 /** @type {?} */
NgContentAst.prototype.index;
 /** @type {?} */
NgContentAst.prototype.ngContentIndex;
 /** @type {?} */
NgContentAst.prototype.sourceSpan;
}

export type PropertyBindingType = number;
export let PropertyBindingType: any = {};
PropertyBindingType.Property = 0;
PropertyBindingType.Attribute = 1;
PropertyBindingType.Class = 2;
PropertyBindingType.Style = 3;
PropertyBindingType.Animation = 4;
PropertyBindingType[PropertyBindingType.Property] = "Property";
PropertyBindingType[PropertyBindingType.Attribute] = "Attribute";
PropertyBindingType[PropertyBindingType.Class] = "Class";
PropertyBindingType[PropertyBindingType.Style] = "Style";
PropertyBindingType[PropertyBindingType.Animation] = "Animation";


/**
 * A visitor for {@link TemplateAst} trees that will process each node.
 */
export interface TemplateAstVisitor {
  // Returning a truthy value from `visit()` will prevent `templateVisitAll()` from the call to
  // the typed method and result returned will become the result included in `visitAll()`s
  // result array.
  visit?(ast: TemplateAst, context: any): any;

  visitNgContent(ast: NgContentAst, context: any): any;
  visitEmbeddedTemplate(ast: EmbeddedTemplateAst, context: any): any;
  visitElement(ast: ElementAst, context: any): any;
  visitReference(ast: ReferenceAst, context: any): any;
  visitVariable(ast: VariableAst, context: any): any;
  visitEvent(ast: BoundEventAst, context: any): any;
  visitElementProperty(ast: BoundElementPropertyAst, context: any): any;
  visitAttr(ast: AttrAst, context: any): any;
  visitBoundText(ast: BoundTextAst, context: any): any;
  visitText(ast: TextAst, context: any): any;
  visitDirective(ast: DirectiveAst, context: any): any;
  visitDirectiveProperty(ast: BoundDirectivePropertyAst, context: any): any;
}
/**
 *  Visit every node in a list of {@link TemplateAst}s with the given {@link TemplateAstVisitor}.
 * @param {?} visitor
 * @param {?} asts
 * @param {?=} context
 * @return {?}
 */
export function templateVisitAll(
    visitor: TemplateAstVisitor, asts: TemplateAst[], context: any = null): any[] {
  const /** @type {?} */ result: any[] = [];
  const /** @type {?} */ visit = visitor.visit ?
      (ast: TemplateAst) => visitor.visit(ast, context) || ast.visit(visitor, context) :
      (ast: TemplateAst) => ast.visit(visitor, context);
  asts.forEach(ast => {
    const /** @type {?} */ astResult = visit(ast);
    if (astResult) {
      result.push(astResult);
    }
  });
  return result;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
